import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Typography } from '../../theme';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme/useTheme';
import {
  REPORT_DETAILS_MAX_LENGTH,
  REVIEW_REPORT_REASONS,
  reportPartnerReview,
  reviewErrorKey,
  type ReviewReportReason,
} from './reviewsApi';
import { reviewPalette } from './StarRating';

// Report someone else's review to the Tonyte team (reports_reviews, handled in
// Profile → Reported reviews by admins).
export default function ReportReviewSheet({ reviewId, onClose, onReported }: {
  reviewId: string | null;
  onClose: () => void;
  onReported: () => void;
}) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  const [reason, setReason] = useState<ReviewReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  const [errorKey, setErrorKey] = useState('');

  useEffect(() => {
    if (!reviewId) return;
    setReason(null);
    setDetails('');
    setSending(false);
    setErrorKey('');
  }, [reviewId]);

  const send = async () => {
    if (!reviewId || !reason || sending) return;
    setSending(true);
    setErrorKey('');
    try {
      await reportPartnerReview(reviewId, reason, details);
      onReported();
    } catch (error) {
      console.warn('[Tonyte][PartnerReviews] report failed', { reviewId, error });
      setErrorKey(reviewErrorKey(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal visible={Boolean(reviewId)} transparent animationType="slide" onRequestClose={onClose} statusBarTranslucent>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel={t('common.cancel')} />
        <View style={[styles.sheet, { backgroundColor: palette.sheet, borderColor: palette.border }]} accessibilityViewIsModal>
          <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={styles.content} bounces={false}>
            <Text style={[styles.title, { color: palette.text }]} accessibilityRole="header">{t('partnerReviews.reportTitle')}</Text>
            <Text style={[styles.label, { color: palette.muted }]}>{t('partnerReviews.reportReason')}</Text>
            {REVIEW_REPORT_REASONS.map(item => (
              <TouchableOpacity
                key={item}
                onPress={() => setReason(item)}
                style={[styles.reason, { borderColor: reason === item ? Colors.sunset : palette.border, backgroundColor: palette.surface }]}
                accessibilityRole="radio"
                accessibilityState={{ checked: reason === item }}
                testID={`report-reason-${item}`}
              >
                <Icon name={reason === item ? 'check-circle' : 'circle'} size={18} color={reason === item ? Colors.sunset : palette.faint} />
                <Text style={[styles.reasonText, { color: palette.text }]}>{t(`partnerReviews.reasons.${item}`)}</Text>
              </TouchableOpacity>
            ))}
            <Text style={[styles.label, { color: palette.muted }]}>{t('partnerReviews.reportDetails')}</Text>
            <TextInput
              value={details}
              onChangeText={setDetails}
              multiline
              maxLength={REPORT_DETAILS_MAX_LENGTH}
              textAlignVertical="top"
              style={[styles.input, { color: palette.text, borderColor: palette.border, backgroundColor: palette.input }]}
              accessibilityLabel={t('partnerReviews.reportDetails')}
            />
            {errorKey ? <Text style={styles.error} accessibilityLiveRegion="polite">{t(errorKey)}</Text> : null}
            <View style={styles.buttons}>
              <TouchableOpacity style={[styles.button, { backgroundColor: palette.surfaceStrong }]} onPress={onClose} accessibilityRole="button">
                <Text style={[styles.secondaryText, { color: palette.text }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.primary, (!reason || sending) && styles.dimmed]}
                onPress={send}
                disabled={!reason || sending}
                accessibilityRole="button"
                testID="report-submit"
              >
                {sending ? <ActivityIndicator color="#fff" /> : <Text style={styles.primaryText}>{t('partnerReviews.reportSubmit')}</Text>}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(2,4,10,0.6)' },
  sheet: { borderTopLeftRadius: 26, borderTopRightRadius: 26, borderWidth: 1, borderBottomWidth: 0, maxHeight: '92%' },
  content: { padding: 20, paddingBottom: 30, gap: 10 },
  title: { fontSize: Typography.lg, fontWeight: Typography.extrabold, textAlign: 'center' },
  label: { fontSize: 12, fontWeight: Typography.bold, marginTop: 4 },
  reason: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 48, borderWidth: 1, borderRadius: 14, paddingHorizontal: 14 },
  reasonText: { fontSize: 14, fontWeight: Typography.semibold, flex: 1 },
  input: { minHeight: 80, maxHeight: 160, borderWidth: 1, borderRadius: 14, padding: 12, fontSize: 14 },
  error: { color: '#F87171', fontSize: 13, textAlign: 'center', fontWeight: Typography.semibold },
  buttons: { flexDirection: 'row', gap: 10, marginTop: 6 },
  button: { flex: 1, minHeight: 50, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  primary: { backgroundColor: Colors.sunset },
  dimmed: { opacity: 0.6 },
  primaryText: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  secondaryText: { fontSize: 15, fontWeight: Typography.semibold },
});
